import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';

const EMPTY = {
  idPersona: 0,
  idRedSocial: 0,
  nikName: '',
  url: '',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  fkValue = 0,
  onClose,
  onSubmit,
  inline = false,
}) {
  // usamos el fk como idPersona interno (no se muestra)
  EMPTY.idPersona = fkValue;
  const [form, setForm] = useState(EMPTY);

  // 👉 catálogo de redes sociales
  const [redesSociales, setRedesSociales] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [errorCats, setErrorCats] = useState(null);

  const [errors, setErrors] = useState({
    idRedSocial: '',
    nikName: '',
    url: '',
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idRedSocial': {
        const num = Number(value) || 0;
        if (!num || num <= 0) error = 'Selecciona una red social.';
        break;
      }
      case 'nikName':
        if (!value) error = 'Captura el usuario o nick.';
        break;
      case 'url':
        if (!value) error = 'Captura la URL.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateAll = () => {
    const okRed = validateField('idRedSocial', form.idRedSocial);
    const okNick = validateField('nikName', form.nikName);
    const okUrl = validateField('url', form.url);
    return okRed && okNick && okUrl;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    validateField(field, value);
  };

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && data) {
      const filled = {
        idPersonasRedesSociales: data.idPersonasRedesSociales ?? 0,
        idPersona: data.idPersona ?? fkValue ?? 0,
        idRedSocial: data.idRedSocial ?? 0,
        nikName: data.nikName ?? '',
        url: data.url ?? data.url ?? '',
      };
      setForm(filled);
      validateField('idRedSocial', filled.idRedSocial);
      validateField('nikName', filled.nikName);
      validateField('url', filled.url);
    } else {
      setForm({
        ...EMPTY,
        idPersona: fkValue || 0,
      });
      setErrors({ idRedSocial: '', nikName: '', url: '' });
    }
  }, [mode, data, open, fkValue]);

  const isSaveDisabled =
    !form.idRedSocial ||
    !form.nikName?.trim() ||
    !form.url?.trim() ||
    !!errors.idRedSocial ||
    !!errors.nikName ||
    !!errors.url;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '.25rem',
  };

  // Cargar catálogo de redes sociales cuando se abre el modal
  useEffect(() => {
    if (!open) return;

    let canceled = false;

    async function loadRedes() {
      setLoadingCats(true);
      setErrorCats(null);
      try {
        const res = await axios.get(ENDPOINTS.redSocial);
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        const redesNorm = raw.map((x) => Models.redSocial(x));

        if (!canceled) {
          setRedesSociales(redesNorm);
        }
      } catch (err) {
        console.error('Error cargando catálogo de redes sociales', err);
        if (!canceled) setErrorCats('No se pudieron cargar las redes sociales.');
      } finally {
        if (!canceled) setLoadingCats(false);
      }
    }

    loadRedes();

    return () => {
      canceled = true;
    };
  }, [open]);

  if (!open) return null;

  // 👇 Usamos nombreRedSocial del normalizer Cat_RedesSociales
  function labelRedSocial(r) {
    if (!r) return '';
    return r.nombreRedSocial || `Red social ${r.idRedSocial}`;
  }

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}
    >
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva red social de persona' : 'Editar red social de persona'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
        ✕
      </button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      {/* 🔹 Select de Red social (ID como value) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Red social:
        </label>
        <br />
        <br />
        <select
          className={`infoField${errors.idRedSocial ? ' infoField--error' : ''}`}
          value={form.idRedSocial || 0}
          onChange={(e) =>
            handleChange('idRedSocial', e.target.value ? Number(e.target.value) : 0)
          }
        >
          <option value={0} disabled>
            {loadingCats ? 'Cargando redes…' : 'Seleccione una red social'}
          </option>
          {redesSociales.map((r) => (
            <option key={r.idRedSocial} value={r.idRedSocial}>
              {labelRedSocial(r)}
            </option>
          ))}
        </select>
      </div>

      {/* Usuario / Nick */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Usuario / Nick:
        </label>
        <br />
        <br />
        <input
          className={`infoField${errors.nikName ? ' infoField--error' : ''}`}
          value={form.nikName}
          onChange={(e) =>
            handleChange('nikName', e.target.value)
          }
          maxLength={50}
        />
      </div>

      {/* URL */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          URL:
        </label>
        <br />
        <br />
        <input
          className={`infoField${errors.url ? ' infoField--error' : ''}`}
          type="url"
          value={form.url}
          onChange={(e) =>
            handleChange('url', e.target.value)
          }
          maxLength={200}
        />
        {errors.idRedSocial && (
          <small style={errorStyle}>{errors.idRedSocial}</small>
        )}
        {errors.nikName && <small style={errorStyle}>{errors.nikName}</small>}
        {errors.url && <small style={errorStyle}>{errors.url}</small>}
      </div>

      {errorCats && (
        <div style={{ gridColumn: '1 / -1', marginTop: '.4rem' }}>
          <p style={{ color: '#b91c1c', fontSize: '.8rem' }}>{errorCats}</p>
        </div>
      )}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>
        Cancelar
      </button>
      <button
        className="btn-action"
        onClick={() => {
          if (!validateAll()) return;
          onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div
        className="roles-inline-card"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
